import { useState, useEffect } from "react";
import { useAuth } from "../../context/AuthContext";
import { format, subDays } from "date-fns";
import { BarChart, Bar, XAxis, Tooltip, ResponsiveContainer, Cell } from "recharts";
import { motion, AnimatePresence } from "framer-motion";
import { useLanguage } from "../../context/LanguageContext";
import { Lock, Heart } from "lucide-react";
import { useNavigate } from "react-router-dom";

import api from "../../api/axios";
import tracker from "../../utils/sanctuary-tracker";
import { MOOD_SCORES, COLORS } from "../../lib/constants";

export default function MoodGraph({ refreshTrigger }) {
    const { user } = useAuth();
    const { t } = useLanguage();
    const navigate = useNavigate();
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [range, setRange] = useState(7); // 7 or 30
    const [showPaywall, setShowPaywall] = useState(false);

    useEffect(() => {
        if (range === 30) {
            tracker.track('premium_feature_lock_hit', { feature: 'mood_history_30d' });
            setShowPaywall(true);
            // We still show the 7d data in the background or empty state
        } else {
            setShowPaywall(false);
        }

        if (!user) {
            // 🧘 Guest Support: Load from local storage
            const guestMoods = JSON.parse(localStorage.getItem('ayi_guest_moods') || '[]');
            const processed = guestMoods.slice(-range).map(m => ({
                day: format(new Date(m.createdAt), "dd MMM"),
                mood: m.mood,
                score: m.score || MOOD_SCORES[m.mood] || 3,
                color: COLORS[m.mood] || "#9ca3af"
            }));
            setData(processed);
            setLoading(false);
            return;
        }

        api.get("/moods/mine")
            .then(res => {
                const moods = res.data;
                if (!Array.isArray(moods)) {
                    setData([]);
                    setLoading(false);
                    return;
                }
                // If range is 30, we only show 7 unless they are "unlocked"
                const displayRange = range === 30 ? 7 : range;
                const processed = moods.slice(-displayRange).map(m => ({
                    day: format(new Date(m.createdAt), range === 7 ? "EEE" : "dd MMM"),
                    mood: m.mood,
                    score: m.score || MOOD_SCORES[m.mood] || 3,
                    color: COLORS[m.mood] || "#9ca3af"
                }));
                setData(processed);
                setLoading(false);
            })
            .catch(err => {
                console.error("Failed to load mood graph", err);
                setLoading(false);
                setData([]);
            });
    }, [user, refreshTrigger, range]);

    if (loading) return <div className="h-48 flex items-center justify-center text-xs text-gray-400">{t('dash_mood_graph_loading') || "Loading graph..."}</div>;
    
    const CustomTooltip = ({ active, payload }) => {
        if (active && payload && payload.length) {
            return (
                <div className="bg-white dark:bg-gray-800 p-2 shadow-lg rounded-lg border border-gray-100 dark:border-gray-700 text-xs">
                    <p className="font-semibold">{payload[0].payload.day}</p>
                    <p style={{ color: payload[0].payload.color }}>{t('mood_' + payload[0].payload.mood.toLowerCase()) || payload[0].payload.mood}</p>
                </div>
            );
        }
        return null;
    };

    return (
        <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="w-full min-h-[14rem] bg-white dark:bg-gray-800 rounded-2xl p-4 shadow-sm border border-gray-100 dark:border-gray-700 flex flex-col relative overflow-hidden"
        >
            <div className="flex justify-between items-center mb-2">
                <h3 className="text-sm font-bold text-gray-700 dark:text-gray-300">{t('pc_title')}</h3>
                <div className="flex bg-gray-100 dark:bg-gray-900 p-0.5 rounded-lg">
                    <button 
                        onClick={() => setRange(7)}
                        className={`px-3 py-1 text-[10px] font-medium rounded-md transition-all ${range === 7 ? 'bg-white dark:bg-gray-800 shadow-sm text-brand-blue' : 'text-gray-500 hover:text-gray-700'}`}
                    >
                        7D
                    </button>
                    <button 
                        onClick={() => setRange(30)}
                        className={`px-3 py-1 text-[10px] font-medium rounded-md transition-all ${range === 30 ? 'bg-white dark:bg-gray-800 shadow-sm text-brand-blue' : 'text-gray-500 hover:text-gray-700'}`}
                    >
                        30D
                    </button>
                </div>
            </div>

            <div className="flex-1 w-full mt-2 min-w-0 relative">
                {data.length === 0 ? (
                    <div className="h-32 flex items-center justify-center text-xs text-gray-400 p-4 text-center">
                        {t('dash_mood_graph_empty')}
                    </div>
                ) : (
                    <ResponsiveContainer width="99%" height={120} minWidth={1} minHeight={1}>
                        <BarChart data={data} barSize={range === 7 ? 20 : 8}>
                            <XAxis dataKey="day" axisLine={false} tickLine={false} tick={{ fill: '#9ca3af', fontSize: 10 }} />
                            <Tooltip content={<CustomTooltip />} cursor={{ fill: 'transparent' }} />
                            <Bar dataKey="score" radius={[4, 4, 4, 4]}>
                                {data.map((entry, index) => (
                                    <Cell key={`cell-${index}`} fill={entry.color} />
                                ))}
                            </Bar>
                        </BarChart>
                    </ResponsiveContainer>
                )}

                {/* 🔒 PAYWALL OVERLAY */}
                <AnimatePresence>
                    {showPaywall && (
                        <motion.div 
                            initial={{ opacity: 0, backdropFilter: "blur(0px)" }}
                            animate={{ opacity: 1, backdropFilter: "blur(4px)" }}
                            exit={{ opacity: 0, backdropFilter: "blur(0px)" }}
                            className="absolute inset-0 bg-white/60 dark:bg-gray-800/60 flex flex-col items-center justify-center p-4 text-center z-10"
                        >
                            <div className="bg-white dark:bg-gray-900 p-3 rounded-full shadow-lg mb-3">
                                <Lock className="w-5 h-5 text-brand-blue" />
                            </div>
                            <h4 className="text-xs font-bold mb-1">{t('premium_history_title') || "Unlock Your Journey"}</h4>
                            <p className="text-[10px] text-gray-500 dark:text-gray-400 mb-4 max-w-[180px]">
                                {t('premium_history_desc') || "Support the sanctuary to view 30-day trends and deep emotional insights."}
                            </p>
                            <button 
                                onClick={() => navigate('/sustain')}
                                className="flex items-center gap-2 px-4 py-2 bg-brand-blue text-white text-[10px] font-bold rounded-xl hover:opacity-90 shadow-md transition-all active:scale-95"
                            >
                                <Heart className="w-3 h-3" />
                                {t('premium_history_cta') || "Support Us"}
                            </button>
                        </motion.div>
                    )}
                </AnimatePresence>
            </div>
        </motion.div>
    );
}
